import builtins from './templates.json'

const builtinById = new Map(builtins.map((template) => [template.id, template]))
const LIGHT_ROLES = new Set([
  '--bg', '--bg-elev', '--text', '--text-body', '--text-dim',
  '--pill-bg', '--pill-border', '--divider', '--start', '--end',
])
const clone = (value) => JSON.parse(JSON.stringify(value))

function legacyVariables(template, builtin, mode) {
  const authored = template.cssVars || []
  const defaults = builtin?.variants?.[mode]?.cssVars || []
  const values = new Map(defaults.map((variable) => [variable.key, variable]))
  const baseValues = mode === 'light'
    ? new Map((builtin?.cssVars || []).map((variable) => [variable.key, variable.value]))
    : null
  for (const variable of authored) {
    const themed = values.get(variable.key)
    const useLightPreset = mode === 'light' && themed
      && (LIGHT_ROLES.has(variable.key) || baseValues.get(variable.key) === variable.value)
    values.set(variable.key, useLightPreset
      ? { ...variable, value: themed.value }
      : variable)
  }
  return [...values.values()]
}

function sameVariables(a = [], b = []) {
  if (a.length !== b.length) return false
  const values = new Map(a.map((variable) => [variable.key, variable.value]))
  return b.every((variable) => values.has(variable.key) && values.get(variable.key) === variable.value)
}

/** Fill legacy mode fields without replacing authored artwork or explicit variants. */
export function normalizeTemplate(template) {
  if (!template) return template
  const builtin = builtinById.get(template.id)
  // A saved copy still carrying a retired builtin revision (same html, css and
  // variable values) was never customized, so it follows the current builtin.
  // Recolored, rewritten or renamed copies keep what the operator authored.
  const retired = builtin && (builtin.previousArtwork || []).find((artwork) => artwork.htmlCode === template.htmlCode
    && artwork.cssCode === template.cssCode && sameVariables(artwork.cssVars, template.cssVars)
    && (!template.variants || Object.keys(template.variants).length === 0 || JSON.stringify(artwork.variants ?? {}) === JSON.stringify(template.variants)))
  const source = retired
    ? {
      ...template,
      name: template.name && template.name !== retired.name ? template.name : builtin.name,
      description: template.description && template.description !== retired.description ? template.description : builtin.description,
      htmlCode: builtin.htmlCode,
      cssCode: builtin.cssCode,
      cssVars: clone(builtin.cssVars),
      variants: clone(builtin.variants),
    }
    : template
  const normalized = clone(source)
  delete normalized.previousArtwork
  if (source.nativeAction === undefined && builtin?.nativeAction) normalized.nativeAction = builtin.nativeAction
  normalized.isFallback = template.isFallback === true || template.id === 't-default'
  normalized.variants = { ...normalized.variants }
  for (const mode of ['light', 'dark']) {
    const variant = source.variants?.[mode]
    const defaults = builtin?.variants?.[mode]
    normalized.variants[mode] = {
      ...clone(variant || {}),
      cssVars: clone(variant?.cssVars ?? legacyVariables(source, builtin, mode)),
      cssCode: variant?.cssCode ?? defaults?.cssCode ?? '',
      branding: clone(variant?.branding ?? defaults?.branding ?? {}),
    }
  }
  return normalized
}

/** Preserve configured catalogs; only an absent/empty catalog selects builtins. */
export function normalizeTemplates(templates) {
  const normalized = (templates?.length ? templates : builtins).map(normalizeTemplate)
  if (!normalized.some((template) => template.isFallback)) {
    normalized.push(normalizeTemplate(builtinById.get('t-default')))
  }
  return normalized
}

// Normalizing deep-clones the template, and every card render and srcdoc build
// asks for its variant, so the result is cached per template object
// (templates are replaced, never edited in place). Every caller gets the same
// variant object, so it is frozen: a caller that edits it throws instead of
// restyling every card with that template.
const normalizedTemplates = new WeakMap()
const EMPTY_VARIANT = deepFreeze({ cssVars: [], cssCode: '', branding: {} })

function deepFreeze(value) {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value)
    for (const child of Object.values(value)) deepFreeze(child)
  }
  return value
}

export function getNormalizedTemplate(template) {
  const cacheable = Boolean(template) && typeof template === 'object'
  let normalized = cacheable ? normalizedTemplates.get(template) : undefined
  if (!normalized) {
    normalized = normalizeTemplate(template)
    if (normalized) deepFreeze(normalized.variants)
    if (cacheable) normalizedTemplates.set(template, normalized)
  }
  return normalized
}

export function getTemplateVariant(template, colorMode = 'dark') {
  const normalized = getNormalizedTemplate(template)
  return normalized?.variants[colorMode === 'light' ? 'light' : 'dark'] || EMPTY_VARIANT
}

export function getFallbackTemplate(templates) {
  return normalizeTemplates(templates).find((template) => template.isFallback)
}
